import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen,
  Clock,
  ShieldCheck,
  Inbox,
  ArrowUpRight,
  Sparkles,
  TrendingUp,
  Phone,
  Mail,
  Send,
  Cpu,
  Database,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Play,
  CheckSquare,
  Lock,
} from "lucide-react";

function Stat({ label, value, icon: Icon, tint = "indigo", testId }) {
  const tintMap = {
    indigo: "from-[#4F46E5]/25 to-[#4F46E5]/0 text-indigo-300",
    cyan: "from-[#06B6D4]/25 to-[#06B6D4]/0 text-cyan-300",
    amber: "from-amber-500/25 to-amber-500/0 text-amber-300",
    emerald: "from-emerald-500/25 to-emerald-500/0 text-emerald-300",
    rose: "from-rose-500/25 to-rose-500/0 text-rose-300",
  };
  return (
    <GlassCard hoverable className="p-6 relative overflow-hidden" data-testid={testId}>
      <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br ${tintMap[tint]} blur-3xl`} />
      <div className="flex items-center justify-between relative">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#9AA4B2]">{label}</div>
          <div className="mt-2 text-3xl md:text-4xl font-display font-semibold text-white">{value ?? "—"}</div>
        </div>
        <div className={`w-11 h-11 rounded-2xl glass flex items-center justify-center ${tintMap[tint].split(" ").pop()}`}>
          <Icon size={20} />
        </div>
      </div>
    </GlassCard>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [ownership, setOwnership] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Custom Power Dashboard States
  const [connectors, setConnectors] = useState(null);
  const [iotEvents, setIotEvents] = useState([]);
  const [auditScore, setAuditScore] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastTarget, setBroadcastTarget] = useState("all");
  const [dispatchResult, setDispatchResult] = useState(null);
  const [iotTargetSop, setIotTargetSop] = useState("");
  const [iotSensorReading, setIotSensorReading] = useState("High Temp Alert (24.2°C)");
  const [selectedDevice, setSelectedDevice] = useState("ESP32-SOP-A3B");
  const [iotTriggerResult, setIotTriggerResult] = useState(null);

  // Syncing Indicators (To satisfy backend live confirmation request)
  const [syncing, setSyncing] = useState(false);
  const [syncedRecently, setSyncedSyncedRecently] = useState(false);

  // Load standard + advanced metrics
  const loadDashboardData = async () => {
    setSyncing(true);
    try {
      const [s, sops, own, conn, events, verification] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/sops"),
        api.get("/dashboard/ownership"),
        api.get("/connectors/status"),
        api.get("/connectors/iot/events"),
        api.post("/connectors/audit-proofs/verify")
      ]);
      setStats(s.data);
      setRecent(sops.data.slice(0, 6));
      setOwnership(own.data.rows.filter((r) => r.overdue).slice(0, 5));
      setConnectors(conn.data);
      setIotEvents(events.data);
      setAuditScore(verification.data);
      
      setSyncedSyncedRecently(true);
      setTimeout(() => setSyncedSyncedRecently(false), 3000);
    } catch (e) {
      console.error("Dashboard loaded with errors", e);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Recalculate Cryptographic Proof Signature ledger
  const runCryptoAuditRecalculation = async () => {
    setRecalculating(true);
    try {
      const res = await api.post("/connectors/audit-proofs/verify");
      setAuditScore(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => setRecalculating(false), 1200);
    }
  };

  // Simulate PWA & WhatsApp Broadcast High-Priority Dispatch
  const triggerBroadcastAlert = async (e) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;
    try {
      const [pwaRes, waRes] = await Promise.all([
        api.post("/connectors/push/notify", {
          title: "🚨 URGENT OPERATIONAL ALERT",
          message: broadcastMessage
        }),
        api.post("/connectors/whatsapp/notify", {
          message: broadcastMessage
        })
      ]);
      setDispatchResult({
        success: true,
        dispatched_to_pwa: pwaRes.data.dispatched_to_pwa,
        whatsapp_recipient: waRes.data.recipient,
        timestamp: new Date().toLocaleTimeString()
      });
      setBroadcastMessage("");
      loadDashboardData(); // reload stats for notifications unread count
      setTimeout(() => setDispatchResult(null), 5000);
    } catch (err) {
      console.error(err);
    }
  };

  // Simulate Microcontroller Sensor SOP launch trigger
  const triggerIotAction = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/connectors/iot/trigger", {
        device_id: selectedDevice,
        sensor_reading: iotSensorReading,
        sop_id: iotTargetSop || undefined
      });
      setIotTriggerResult({
        success: true,
        triggered_sop: res.data.event.triggered_sop,
        hash_seal: res.data.event.hash_seal
      });
      loadDashboardData();
      setTimeout(() => setIotTriggerResult(null), 10000); // visible for 10 seconds for verification
    } catch (err) {
      console.error(err);
    }
  };

  const hello = user?.name?.split(" ")[0] || "there";

  return (
    <div className="space-y-6 pb-10" data-testid="dashboard-page">
      {/* Header with quick shortcuts */}
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-cyan-300">
            <Sparkles size={12} /> {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="mt-1.5 font-display text-3xl md:text-4xl font-semibold text-white leading-tight tracking-tight">
            Command Center
          </h1>
          <p className="mt-1.5 text-sm text-[#9AA4B2] max-w-xl">
            Connectors, cryptographic audit verification, and priority alerts.
          </p>
        </div>
        
        {/* Dynamic connection synchronization feedback */}
        <div className="flex flex-wrap items-center gap-2">
          {syncedRecently && (
            <span className="text-[10px] text-emerald-400 font-bold tracking-wide flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5 animate-pulse">
              <CheckCircle2 size={11} /> Sync Complete
            </span>
          )}
          {syncing && (
            <span className="text-[10px] text-cyan-300 font-bold tracking-wide flex items-center gap-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-3 py-1.5">
              <RefreshCw size={11} className="animate-spin" /> Live Sync active...
            </span>
          )}
          <button
            onClick={() => navigate("/profile")}
            className="px-4 py-2 rounded-full glass border border-white/10 hover:bg-white/[0.04] text-xs font-medium text-[#9AA4B2] hover:text-white transition flex items-center gap-1.5"
          >
            User settings
          </button>
          <button
            onClick={loadDashboardData}
            disabled={syncing}
            className="px-4 py-2 rounded-full bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-xs font-medium text-white shadow-lg shadow-indigo-500/20 hover:opacity-90 transition flex items-center gap-1.5"
          >
            {syncing ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Sync Dashboard
          </button>
        </div>
      </section>

      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total SOPs" value={stats?.total_sops} icon={BookOpen} tint="indigo" testId="stat-total" />
        <Stat label="Approved" value={stats?.approved} icon={ShieldCheck} tint="emerald" testId="stat-approved" />
        <Stat label="In Review" value={stats?.in_review} icon={Clock} tint="amber" testId="stat-in-review" />
        <Stat label="Expired / Overdue" value={(stats?.expired ?? 0) + (stats?.due_soon ?? 0)} icon={TrendingUp} tint="rose" testId="stat-expired" />
      </section>

      {/* Advanced Verification and Action Items Block */}
      <section className="grid md:grid-cols-3 gap-6">
        
        {/* Dynamic Compliance Audit Score - Crypto verification Dial */}
        <GlassCard className="p-6 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl" />
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
                <Lock size={16} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white tracking-wide">Crypto Audit Security</h3>
                <p className="text-[10px] text-[#5B6472]">SHA-256 Signature Verification</p>
              </div>
            </div>
            
            <div className="my-5 flex flex-col items-center justify-center">
              <div className="relative flex items-center justify-center">
                {/* Dial indicator */}
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle cx="64" cy="64" r="54" stroke="rgba(255,255,255,0.04)" strokeWidth="6" fill="transparent" />
                  <circle
                    cx="64"
                    cy="64"
                    r="54"
                    stroke="url(#emerald-gradient)"
                    strokeWidth="6"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 54}
                    strokeDashoffset={2 * Math.PI * 54 * (1 - (auditScore?.audit_readiness_score ?? 100) / 100)}
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="emerald-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10B981" />
                      <stop offset="100%" stopColor="#06B6D4" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="font-display text-3xl font-bold text-white">{auditScore?.audit_readiness_score ?? 100}%</span>
                  <span className="text-[8px] uppercase tracking-widest text-emerald-400 font-medium">VERIFIED SECURE</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-[#9AA4B2] text-center leading-relaxed">
                Recalculated cryptographic seals of all registered e-signatures. 
              </p>
            </div>
          </div>
          
          <button
            onClick={runCryptoAuditRecalculation}
            disabled={recalculating}
            className="w-full mt-2 py-2.5 rounded-xl glass border border-white/10 hover:bg-white/[0.04] text-xs font-semibold text-white transition flex items-center justify-center gap-2"
          >
            {recalculating ? (
              <>
                <RefreshCw size={12} className="animate-spin text-emerald-400" />
                Recalculating Seals...
              </>
            ) : (
              <>
                <ShieldCheck size={12} className="text-emerald-400" />
                Execute RSA Recalculation
              </>
            )}
          </button>
        </GlassCard>

        {/* Actionable Checklists & Next Up Items */}
        <GlassCard className="p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg text-white">Actionable Checklists</h3>
              <p className="text-xs text-[#9AA4B2]">Pending standard compliance actions & physical reviews</p>
            </div>
            <span className="text-xs text-indigo-400 font-semibold flex items-center gap-1">
              <CheckSquare size={13} /> Active Tasks
            </span>
          </div>

          <div className="space-y-3">
            {[
              { id: "task-1", title: "Acknowledge Warehouse receiving protocol v2.1", date: "Due in 2 days", priority: "High", icon: BookOpen },
              { id: "task-2", title: "Approve IT Incident Response Runbook v1.3", date: "Due in 3 days", priority: "Medium", icon: Clock },
              { id: "task-3", title: "Review Cold Storage temperature limits", date: "Due in 5 days", priority: "Low", icon: Cpu }
            ].map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-indigo-300">
                    <t.icon size={16} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white">{t.title}</h4>
                    <p className="text-[11px] text-[#5B6472] mt-0.5">{t.date} · <span className="text-indigo-400 font-medium">{t.priority} priority</span></p>
                  </div>
                </div>
                <button 
                  onClick={() => navigate(t.id === "task-2" ? "/approvals" : "/library")}
                  className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] text-xs font-semibold text-white transition flex items-center gap-1"
                >
                  Execute
                </button>
              </div>
            ))}
          </div>
        </GlassCard>
      </section>

      {/* Advanced Custom Controls & Microcontroller Hardware Simulator */}
      <section className="grid md:grid-cols-2 gap-6">
        
        {/* Microcontroller Sensor Trigger (IoT Bus Simulator) */}
        <GlassCard className="p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg text-white">ESP32 Hardware Connector</h3>
                <p className="text-xs text-[#9AA4B2]">Simulate hardware sensor/PLC actions launching SOPs</p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
                <Cpu size={18} />
              </div>
            </div>

            <form onSubmit={triggerIotAction} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">
                  1. Select Microcontroller device
                </label>
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
                >
                  <option value="ESP32-SOP-A3B">ESP32-SOP-A3B (Cold Chain Monitor)</option>
                  <option value="ESP32-SOP-F9C">ESP32-SOP-F9C (Warehouse PLC Gate)</option>
                  <option value="ESP32-SOP-ADMIN">Arduino-SOP-992 (Server Room Hub)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">
                  2. Sensor trigger state
                </label>
                <input
                  type="text"
                  value={iotSensorReading}
                  onChange={(e) => setIotSensorReading(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
                  placeholder="e.g. Temperature Spike (24.2°C)"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">
                  3. Link Target SOP (Optional)
                </label>
                <select
                  value={iotTargetSop}
                  onChange={(e) => setIotTargetSop(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
                >
                  <option value="">Standard Escalation Protocol</option>
                  {recent.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-xl bg-gradient-to-br from-[#4F46E5] to-[#2563EB] text-xs font-semibold text-white shadow-lg shadow-indigo-500/20 hover:opacity-90 transition flex items-center justify-center gap-1.5"
              >
                <Play size={12} fill="white" />
                Fire Hardware Sensor Interrupt
              </button>
            </form>
          </div>

          {/* HIGH VISIBILITY Live Diagnostic Output Block */}
          {iotTriggerResult && (
            <div className="mt-4 p-4 rounded-2xl bg-[#0F172A]/90 border border-emerald-500/30 text-xs shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-xl" />
              <div className="flex items-center gap-2 font-bold text-emerald-400 mb-2 text-sm border-b border-white/5 pb-2">
                <CheckCircle2 size={16} className="animate-bounce" />
                Live Screen Diagnostic: Microcontroller Trigger Successful!
              </div>
              <div className="space-y-1.5 font-mono text-[#9AA4B2] text-[11px]">
                <div><span className="text-white">Active Device:</span> {selectedDevice}</div>
                <div><span className="text-white">Sensor Signal:</span> <span className="text-rose-400 font-bold">{iotSensorReading}</span></div>
                <div><span className="text-white">Launched SOP:</span> <strong className="text-cyan-300">{iotTriggerResult.triggered_sop}</strong></div>
                <div className="pt-1.5 border-t border-white/5 text-[10px] text-emerald-400/90 break-all leading-relaxed">
                  <span className="text-white">SHA-256 Ledger Seal:</span> {iotTriggerResult.hash_seal}
                </div>
              </div>
            </div>
          )}
        </GlassCard>

        {/* Messaging & Mobile Push Broadcast Connector Panel */}
        <GlassCard className="p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg text-white">Broadcast Alerts</h3>
                <p className="text-xs text-[#9AA4B2]">Simulate push alerts & WhatsApp business dispatch</p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
                <Send size={18} />
              </div>
            </div>

            <form onSubmit={triggerBroadcastAlert} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">
                  Channel Route
                </label>
                <select
                  value={broadcastTarget}
                  onChange={(e) => setBroadcastTarget(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
                >
                  <option value="all">Simulated SMS & WhatsApp + PWA Push</option>
                  <option value="whatsapp">WhatsApp Business API Only</option>
                  <option value="pwa_push">PWA Web Push Native Alert Only</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">
                  Broadcast Alert Message
                </label>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  rows="4"
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
                  placeholder="Type an urgent update (e.g. IT server backup completed, please check compliance signatures)."
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-xl bg-gradient-to-br from-[#06B6D4] via-[#0891B2] to-[#2563EB] text-xs font-semibold text-white shadow-lg shadow-cyan-500/20 hover:opacity-90 transition flex items-center justify-center gap-1.5"
              >
                <Send size={12} />
                Broadcast Dispatch Block
              </button>
            </form>
          </div>

          {/* Feedback */}
          {dispatchResult && (
            <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300">
              <div className="flex items-center gap-1.5 font-bold mb-1">
                <CheckCircle2 size={14} /> Messaging broadcast completed!
              </div>
              <p>Mobile Native Push: <strong>Active</strong></p>
              <p>Simulated WhatsApp: <strong>{dispatchResult.whatsapp_recipient}</strong></p>
              <span className="text-[10px] text-[#9AA4B2] block mt-1">Dispatched at {dispatchResult.timestamp}</span>
            </div>
          )}
        </GlassCard>

      </section>

      {/* Integration Connections Hub Status Section */}
      <section>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg text-white">Global Connectors Bus Status</h3>
              <p className="text-xs text-[#9AA4B2]">Unified monitoring matrix for physical, enterprise and communication networks</p>
            </div>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-medium">
              System Active
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { name: "WhatsApp Business API", icon: Phone, key: "whatsapp", desc: "HabileCo Gateway v1" },
              { name: "SMTP secure gateway", icon: Mail, key: "email", desc: "Secure SSL, port 587" },
              { name: "PWA Native Push", icon: Send, key: "push", desc: "Web Push standalone PWA" },
              { name: "ESP32 Hardware Connector", icon: Cpu, key: "microcontroller", desc: "Baud rate 115200" },
              { name: "SAP OData Interface", icon: Database, key: "sap_connector", desc: "SAP RFC bridge" }
            ].map((conn) => {
              const info = connectors?.[conn.key];
              const isOnline = info?.status === "online" || info?.status === "listening";
              return (
                <div key={conn.name} className="p-3 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${isOnline ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
                    <span className="text-[11px] font-semibold text-white truncate">{conn.name}</span>
                  </div>
                  <div className="text-[10px] text-[#5B6472]">{conn.desc}</div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </section>

      {/* Recent SOPs + RSS Alerts Feed */}
      <section className="grid lg:grid-cols-3 gap-6">
        
        {/* Recent SOPs */}
        <GlassCard className="p-6 lg:col-span-2" data-testid="recent-sops">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-white">Recent Approved SOPs</h2>
            <button
              className="text-sm text-cyan-300 hover:text-cyan-200"
              onClick={() => navigate("/library")}
              data-testid="recent-view-all"
            >
              View library →
            </button>
          </div>
          <div className="space-y-2">
            {recent.length === 0 && (
              <div className="text-sm text-[#5B6472] py-10 text-center">Nothing to show yet.</div>
            )}
            {recent.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/sops/${s.id}`)}
                className="w-full text-left flex items-center gap-4 p-3 rounded-2xl hover:bg-white/[0.04] transition"
                data-testid={`recent-sop-${s.id}`}
              >
                <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-indigo-300">
                  <BookOpen size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{s.title}</div>
                  <div className="text-xs text-[#5B6472] truncate">
                    {s.department || "—"} · v{s.current_version_number}
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </button>
            ))}
          </div>
        </GlassCard>

        {/* Real-time Hardware Trigger Events Feed */}
        <GlassCard className="p-6 flex flex-col justify-between" data-testid="overdue-panel">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-white">Hardware RSS Feed</h2>
              <span className="text-[10px] text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded font-mono">LIVE FEED</span>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {iotEvents.length === 0 ? (
                <div className="text-sm text-[#5B6472] py-8 text-center">
                  No hardware events detected. Fire a simulated signal above to test.
                </div>
              ) : (
                iotEvents.map((evt) => (
                  <div key={evt._id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/5 text-xs">
                    <div className="flex items-center justify-between text-[#9AA4B2] mb-1">
                      <span className="font-semibold text-cyan-300">{evt.device_id}</span>
                      <span className="text-[10px]">{new Date(evt.timestamp).toLocaleTimeString()}</span>
                    </div>
                    <p className="text-white">Reading: <strong>{evt.sensor_reading}</strong></p>
                    <p className="text-[11px] text-[#5B6472] mt-0.5">SOP: {evt.triggered_sop}</p>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/5">
            <h3 className="text-xs uppercase tracking-widest text-[#5B6472] font-semibold mb-2">Attention Items</h3>
            {ownership.length === 0 ? (
              <div className="text-xs text-[#5B6472]">No overdue SOPs. You&apos;re in the green.</div>
            ) : (
              <ul className="space-y-2">
                {ownership.map((r) => (
                  <li key={r.sop_id} className="flex items-start gap-2 text-xs">
                    <div className="w-1.5 h-2 rounded-full bg-rose-400 mt-1.5 shadow-[0_0_8px_rgba(244,63,94,0.7)]" />
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => navigate(`/sops/${r.sop_id}`)}
                        className="text-white text-xs hover:underline truncate block text-left"
                      >
                        {r.title}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </GlassCard>
        
      </section>
    </div>
  );
}
